import { Cesium } from 'app/cesium';
import { ConfigService } from 'app/config/config.service';
import {
    AbstractRestrictionsService,
    RestrictionState,
} from 'app/licensing/restriction_service_interface';
import { CesiumZone } from 'app/map/geometry/cesium/CesiumZones/CesiumZone';
import { CesiumZoneCollection } from 'app/map/geometry/cesium/CesiumZones/CesiumZoneCollection';
import { CesiumZonePropertyUpdater } from 'app/map/geometry/cesium/CesiumZones/CesiumZonePropertyUpdater';
import { CesiumZoneUpdater } from 'app/map/geometry/cesium/CesiumZones/CesiumZoneUpdater';
import { OffsetManager } from 'app/map/geometry/cesium/OffsetManager';
import { CesiumMap } from 'app/map/map';
import { AssetCollection } from 'app/shared/collections/asset_collection.service';
import { Zone } from 'app/shared/collections/zones/zone';
import { ZoneCollection } from 'app/shared/collections/zones/zone_collection.service';
import { SelectionService } from 'app/shared/selection/SelectionService';
import { SiteStateService } from 'app/shared/site_state.service';
import { throttleCollectionChanges } from 'app/shared/throttled_collection_change_listener';
import { ClockHandler } from 'app/shared/time/clock_handler.service';
import { Toast } from 'app/shared/toast/toast.service';
import { ToastService } from 'app/shared/toast/ToastService';
import { ZoneService } from 'app/shared/zones/zone.service';
import { DisplayNothingEntityVisualiser } from 'app/views/BaseViewController';
import * as _ from 'lodash';
import moment from 'moment-timezone';

const SECONDS_TO_SHOW_ZONE_SHADOW = 120;
export const DISPLAY_NOTHING_ENTITY_VISUALISER = new DisplayNothingEntityVisualiser();

/*
                                                      +-------+
                                                      | Events|
                                                      +---^---+
                                                          |
                                                          | subscribes to lots of things
                                                          | and updates CesiumZones based on state changes
                                                 +--------+--------+
                                       +-------->+CesiumZoneUpdater|
                                       |         +-----------------+
                                       |                                                                            Get our x,y and x,y,z
                                       |                                                                            positions for CesiumZones
Bit of a catch all           +---------+-------+                      +-------------------------+                 +--------------------------+
(formerly CesiumZoneManager) |CesiumZoneService+--------------------->+CesiumZonePropertyUpdater+---------------->+ CesiumZonePositionSampler|
                             +---------+-------+                      +-------------+-----------+                 +--------------------------+
                                       ^                                            ^ Updates specific properties
                                       |                                            | on CesiumZones
                                       |       +--------------------+               |
                                       +-------+CesiumZoneCollection+---------------+
                                               +---------+----------+
                                                         | Holds our CesiumZones
                                                         |
                                                         |
                                                   +-----v----+
                                                   |CesiumZone|
                                                   +----------+
 */
export class CesiumZoneService {
    private readonly _zoneCollection: ZoneCollection;
    private readonly _assetCollection: AssetCollection;
    private readonly _clockHandler: ClockHandler;
    private readonly _selectionService: SelectionService;
    private readonly _zoneShadowEntityDataSource: Cesium.CustomDataSource;
    private readonly _cesiumMap: CesiumMap;
    private readonly _siteStateService: SiteStateService;
    private readonly _primitiveCollection: Cesium.PrimitiveCollection;
    private readonly _cesiumLabelCollection: Cesium.LabelCollection;
    private readonly _currentZoneHistoryUnixTimeForZoneID: { [id: number]: number };
    private readonly _cesiumZoneCollection: CesiumZoneCollection;
    _zoneIdsRequiringAShadow: number[];

    private _toastService: ToastService;
    private _cesiumZonePropertyUpdater: CesiumZonePropertyUpdater;
    private _cesiumZoneUpdater: CesiumZoneUpdater;
    private _offsetManager: OffsetManager;

    private _zoneRestriction: RestrictionState;
    public Ready: Promise<void>;

    constructor(
        zoneCollection: ZoneCollection,
        assetCollection: AssetCollection,
        clockHandler: ClockHandler,
        cesiumMap: CesiumMap,
        selectionService: SelectionService,
        shouldInitialise: boolean = true,
        siteStateService: SiteStateService,
        toastService: ToastService,
        restrictionsService: AbstractRestrictionsService,
        cesiumLabelCollection: Cesium.LabelCollection,
        cesiumZonePropertyUpdater: CesiumZonePropertyUpdater,
        cesiumZoneUpdater: CesiumZoneUpdater,
        cesiumZoneCollection: CesiumZoneCollection,
        offsetManager: OffsetManager,
    ) {
        this._zoneCollection = zoneCollection;
        this._assetCollection = assetCollection;
        this._clockHandler = clockHandler;
        this._cesiumMap = cesiumMap;
        this._selectionService = selectionService;
        // Entity
        this._zoneShadowEntityDataSource = new Cesium.CustomDataSource('zones');
        // Primitive requirements
        this._primitiveCollection = new Cesium.PrimitiveCollection();
        this._cesiumLabelCollection = cesiumLabelCollection;
        this._siteStateService = siteStateService;

        this._cesiumZoneCollection = cesiumZoneCollection;
        this._currentZoneHistoryUnixTimeForZoneID = {};
        this._zoneIdsRequiringAShadow = [];
        this._toastService = toastService;

        this._cesiumZonePropertyUpdater = cesiumZonePropertyUpdater;
        this._cesiumZoneUpdater = cesiumZoneUpdater;
        this._offsetManager = offsetManager;

        this._zoneRestriction = { disabled: true, hidden: true };
        restrictionsService.subscribeToChanges((restrictions) => {
            this._zoneRestriction = AbstractRestrictionsService.get(restrictions?.zones);
            this.redrawAllZonesOnMap();
        });

        // This should only run if the zone service should be created (currently set in config)
        if (shouldInitialise) {
            this.Ready = new Promise((resolve) => {
                this.init().then(() => resolve(null));
            });
        }
    }

    private async init() {
        await Cesium.GroundPrimitive.initializeTerrainHeights();
        this._zoneIdsRequiringAShadow = [];
        this._cesiumMap.viewer.scene.primitives.add(this._primitiveCollection);
        this._cesiumMap.viewer.scene.primitives.add(this._cesiumLabelCollection);
        await this._cesiumMap.viewer.dataSources.add(this._zoneShadowEntityDataSource);

        // Handle changes in zones
        this._zoneCollection.addOnChangedListener((zones) => this._createOrUpdateZones(zones));

        // Don't worry about cleaning this up - it's a service so it never gets destroyed
        // When any collection even remotely sneezes - obliterate the zones on map and make sure they're up to date.
        //  It's a nuclear approach to the fact that you can color/label a zone by a zone stat with a foreign key to
        //  another zone, an ip device or an asset - and we don't have a good way of knowing if anything changes and
        //  who cares about that change.
        throttleCollectionChanges(100, [this._zoneCollection, this._assetCollection], () => {
            this.redrawAllZonesOnMap();
        });

        // Clear out all zones on site selection change
        this._siteStateService.onSiteSelectionChanged(this._deleteAll.bind(this));

        // Handle a tick every second
        this._clockHandler.addOnCursorMovedListener(
            this._onTick.bind(this),
            ConfigService.imsBaseTimeResolution,
        );
    }

    private _hideZones(): boolean {
        return this._zoneRestriction.disabled || this._zoneRestriction.hidden;
    }

    /**
     * Flies to a zone when provided with a zone id
     */
    public flyToZoneById(id: number | string): void {}

    private _createRectangleFromZone(zone: Zone): Cesium.Rectangle {
        const zonePolygon = ZoneService.activePolygonCartesian3s(
            zone,
            this._clockHandler.getCursorTimeAsJulian(),
        );
        return zonePolygon ? Cesium.Rectangle.fromCartesianArray(zonePolygon) : null;
    }

    /**
     * Creates a zone shadow entity if it is required
     */
    public createShadowWhenReady(zone: Zone): void {
        if (ZoneService.latestHistoryPoint(zone)) {
            // if it's an existing zone that had a new history point added, it's already there
            this._createZoneShadow(zone);
        } else {
            // if it's a new zone, the first history point won't have been added yet, need to wait
            this._zoneIdsRequiringAShadow.push(zone.id);
        }
    }

    /**
     * This basically acts as a publicly accessible wrapper for '_createOrUpdateZones'
     */
    public redrawAllZonesOnMap(): void {
        this._createOrUpdateZones(this._zoneCollection.getAllAsArray());
    }

    /**
     * This is a fairly brutal create or update of all zones and is intended to be run when there is a significant
     * change, such as a change to the zone collection. This will catch situations where a zone is added/deleted
     */
    private _createOrUpdateZones(zones: { [id: number]: Zone }) {
        const hideZones = this._hideZones();

        _.forEach(zones, (zone: Zone) => {
            // Remove zones that have been deleted from IMS or hidden due to license restrictions
            if (zone.isDeleted || zone.removed || hideZones) {
                this.delete(zone);
            } else {
                this._createOrUpdateCesiumZone(zone);
            }
            // Handle shadows for zones that have been created in the future
            if (
                ZoneService.latestHistoryPoint(zone) &&
                this._zoneIdsRequiringAShadow.includes(zone.id)
            ) {
                this._createZoneShadow(zone);
                _.remove(this._zoneIdsRequiringAShadow, (id) => id === zone.id);
            }
        });
    }

    /**
     * Creates a CesiumZone from an ims Zone if it doesn't already exist, otherwise updates it
     */
    private _createOrUpdateCesiumZone(zone: Zone) {
        // if the zone doesn't have a type yet, we don't want to render it
        // it will come back through when its type has been added
        if (zone.type) {
            ZoneService.createHistoryIntervals(zone);

            if (!this._cesiumZoneCollection.get(zone.id)) {
                this.create(zone);
            } else {
                this._cesiumZoneUpdater.fullZoneUpdate(zone);
            }
        }
    }

    /**
     * The intent is for this function to be called on tick. It handles updating everything that needs to be updated
     * on a time-basis and applies changes to the CesiumZone objects each tick so that they aren't updating
     * between tick-cycles.
     */
    private _onTick() {
        if (this._hideZones()) {
            return;
        }

        const time = this._clockHandler.getCursorTimeAsJulian();
        this._zoneCollection.getAllAsArray().forEach((zone) => {
            const imsZone = this._zoneCollection.get(zone.id);
            const cesiumZone = this._cesiumZoneCollection.get(zone.id);
            if (!cesiumZone) {
                this.create(imsZone);
            } else {
                this._updateTimeSensitiveZoneValues(cesiumZone, imsZone, time);
                cesiumZone.applyPendingRefreshes();
            }
        });
    }

    /**
     * Updates all values that are likely to change given a change in cesium time such as zone color or label text
     */
    private _updateTimeSensitiveZoneValues(
        cesiumZone: CesiumZone,
        imsZone: Zone,
        time: Cesium.JulianDate,
    ): void {
        // Update the zone color
        this._cesiumZonePropertyUpdater.updateZoneColor(imsZone, cesiumZone, time);

        // Update the polygon positions - don't await this promise
        this._cesiumZonePropertyUpdater.updateZonePolygonPositions(imsZone, cesiumZone, time);

        // Zone (polygon & label) may show or hide depending on available stats
        this._cesiumZonePropertyUpdater.updateShowForZone(imsZone, cesiumZone, time);

        // Update label text
        this._cesiumZonePropertyUpdater.updateZoneLabel(imsZone, cesiumZone, time);
    }

    public get(id: number): CesiumZone {
        return this._cesiumZoneCollection.get(id);
    }

    /**
     * Creates a CesiumZone in the manager for a given ims Zone object
     */
    public create(imsZone: Zone): void {
        // Don't create cesiumZone if it already exists
        if (!this.get(imsZone.id)) {
            const cesiumZone = new CesiumZone(
                imsZone.id,
                this._cesiumLabelCollection,
                this._primitiveCollection,
            );
            this._cesiumZoneCollection.zones[imsZone.id] = cesiumZone;
            this._cesiumZonePropertyUpdater.updateZoneLabel(
                imsZone,
                cesiumZone,
                this._clockHandler.getCursorTimeAsJulian(),
            );
            this._offsetManager.set(
                imsZone.id,
                this._cesiumZoneCollection.zones[imsZone.id],
                OffsetManager.ZINDEX.Zone,
            );
        }

        // Do the initial update of all values
        this._cesiumZoneUpdater.fullZoneUpdate(imsZone);
    }

    /**
     * Creates an id to be used for the zone shadow's Cesium Entity
     */
    private static _zoneShadowEntityId(imsZone: Zone) {
        return `${imsZone.id}_shadow`;
    }

    // This whole business is still currently an Entity as it's far easier to deal with,
    // and it is almost never on the map so it has limited performance impact
    private _createZoneShadow(imsZone: Zone): void {
        const cart3s = ZoneService.latestPolygonCartesian3s(imsZone);
        const closedCart3s = [...cart3s, cart3s[0]];
        // See if we have a current entity.
        let entity = this._zoneShadowEntityDataSource.entities.getById(
            CesiumZoneService._zoneShadowEntityId(imsZone),
        );
        this._currentZoneHistoryUnixTimeForZoneID[imsZone.id] =
            ZoneService.latestHistoryPoint(imsZone).unixTime();
        // If shadow zone does not exist, create it and it's removal listener
        if (!entity) {
            // Add a reference to our removeListener here so the function we pass in can access it
            const removeListener = this._clockHandler.addOnTickListener(() => {
                const removeShadowZoneTime = this._currentZoneHistoryUnixTimeForZoneID[imsZone.id];
                // When our current time has passed our removal time, we can delete this shadow zone
                if (this._clockHandler.getCursorTimeAsUnix() >= removeShadowZoneTime) {
                    // Delete the zone and remove the listener
                    this._removeEntityIfExists(CesiumZoneService._zoneShadowEntityId(imsZone));
                    this._currentZoneHistoryUnixTimeForZoneID[imsZone.id] = undefined;
                    removeListener();
                }
            });
            // Update our reference to this entity
            entity = this._zoneShadowEntityDataSource.entities.add({
                id: CesiumZoneService._zoneShadowEntityId(imsZone),
            } as Cesium.Entity);
        }
        const removeAtMoment = moment(ZoneService.latestHistoryPoint(imsZone).timestamp);
        const secondsUntilRemovalFrom = (timeJulian) => {
            const timeMoment = moment(Cesium.JulianDate.toIso8601(timeJulian));
            return moment.duration(removeAtMoment.diff(timeMoment)).asSeconds();
        };
        const show = new Cesium.CallbackProperty(
            (time) => secondsUntilRemovalFrom(time) < SECONDS_TO_SHOW_ZONE_SHADOW,
            false,
        );

        // Update the entities visuals
        entity.position = new Cesium.ConstantProperty(
            ZoneService.latestPolygonCartesian3s(imsZone)[0],
        );
        entity.polyline = new Cesium.PolylineGraphics({
            show,
            positions: new Cesium.ConstantProperty(closedCart3s),
            material: new Cesium.PolylineDashMaterialProperty(),
        });
        entity.label = new Cesium.LabelGraphics({
            show,
            heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
            eyeOffset: new Cesium.Cartesian3(0, 0, -300),
            text: new Cesium.CallbackProperty((time) => {
                const num = secondsUntilRemovalFrom(time).toFixed();
                return parseInt(num) > 0 ? `${num}s` : '';
            }, false),
            font: '20px Helvetica',
            translucencyByDistance: new Cesium.NearFarScalar(1.5e4, 1.0, 1.5e6, 0.0),
            scaleByDistance: new Cesium.NearFarScalar(1.5e3, 1.0, 1.5e6, 0.0),
        });
    }

    /**
     * This is used for getting zone shadow entities by zone id
     */
    private _getZoneShadowEntityById(id): Cesium.Entity {
        return id ? this._zoneShadowEntityDataSource.entities.getById(id) : undefined;
    }

    /**
     * This is used for removing entities used by this cesiumZoneService such as zone shadows
     */
    private _removeEntityIfExists(id: string): void {
        const entity: Cesium.Entity = this._getZoneShadowEntityById(id);
        if (entity) {
            this._zoneShadowEntityDataSource.entities.remove(entity);
        }
    }

    /**
     * Deletes a zone off the map for a given IMS Zone
     */
    public delete(imsZone: Zone): void {
        const cesiumZone = this.get(imsZone.id);
        if (cesiumZone !== undefined) {
            // Delete it.
            cesiumZone.destroy();
            // Remove from our _cesiumZones object.
            delete this._cesiumZoneCollection.zones[imsZone.id];
            // Remove shadow zone entity for this zone.
            this._removeEntityIfExists(CesiumZoneService._zoneShadowEntityId(imsZone));
        }
    }

    /**
     * Removes all Cesium Zones and related entities/primitives from the map
     */
    private _deleteAll(): void {
        // Entity is still used for zone shadows
        this._zoneShadowEntityDataSource.entities.removeAll();
        // By default the primitive collection will destroy primitives that are
        // removed from the collection
        this._primitiveCollection.removeAll();
        this._cesiumLabelCollection.removeAll();
        this._cesiumZoneCollection.destroy();
        this._zoneIdsRequiringAShadow = [];
    }
}
